import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast, Slide } from 'react-toastify';
import {
  sendNotificationSchema,
  type SendNotificationFormData,
} from '../validation/notification.schemas';
import { notificationsApi } from '../api/notifications.api';
import { usersApi } from '@/domains/users/api/users.api';
import { useRoutesProvider } from '@/providers/provider-hooks';
import { updateFailureToast, toastOneSuccess, toastOneError } from '@/toast';
import { delay, trimStringsFromAnywhere } from '@/shared/utils';
import { getErrorMessage } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';

/**
 * Custom hook for bulk notification form using react-hook-form + Zod
 *
 * Features:
 * - Client-side validation with Zod (receivers, subject, content)
 * - Loads default notification template from backend
 * - Fetches user IDs based on selected roles (PROFESOR/ELEV)
 * - Sends bulk notifications via API
 * - Cancel functionality to reset form
 * - Backend error mapping to form fields
 *
 * Usage:
 * ```tsx
 * const { form, onSubmit, onCancel, isLoading, isSubmitting } = useSendNotificationWithForm();
 * <Form {...form}>
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     <Controller name="sendToTeachers" control={form.control} ... />
 *   </form>
 * </Form>
 * ```
 */
export function useSendNotificationWithForm() {
  const { ROLES } = useRoutesProvider();

  // Initialize form with Zod validation
  const form = useForm<SendNotificationFormData>({
    resolver: zodResolver(sendNotificationSchema),
    defaultValues: {
      sendToTeachers: false,
      sendToPupils: false,
      subject: '',
      content: '',
    },
  });

  // FE-M5: `queryFn` trebuie sa fie un fetcher pur -- React Query decide singur cand ruleaza
  // (retry, refetch, remount), iar un `form.reset` acolo poate sterge un mesaj tastat de admin
  // fara nicio confirmare.
  const { isLoading, refetch: refetchDefault } = useQuery({
    queryKey: ['notifications', 'default'],
    queryFn: () => notificationsApi.getDefault(),
    enabled: false, // Manual trigger only
  });

  // Reset-urile raman EXPLICITE, la cele trei momente voite (mount, dupa trimitere, la
  // reincarcare), pe datele intoarse DIRECT de refetch() -- NU intr-un efect pe `data` din
  // useQuery: React Query face structural sharing implicit, deci `data` isi pastreaza referinta
  // cand raspunsul e identic cu cel anterior (cazul obisnuit -- template-ul nu se schimba des),
  // iar efectul n-ar mai rula. Formularul ar ramane cu mesajul tocmai trimis in loc sa revina la
  // template (regresie fata de comportamentul de azi).
  const populeazaCuTemplate = (
    data: { subject?: string; content?: string } | undefined
  ) => {
    form.reset({
      sendToTeachers: false,
      sendToPupils: false,
      subject: data?.subject || '',
      content: data?.content || '',
    });
  };

  // Load default template on mount
  useEffect(() => {
    const loadDefault = async () => {
      const id = toast.loading('Pagina se incarca...', { transition: Slide });
      try {
        await delay(1000);
        const { data } = await refetchDefault();
        populeazaCuTemplate(data);
        toast.dismiss(id);
      } catch (error: unknown) {
        updateFailureToast(getErrorMessage(error, 'Pagina nu s-a putut incarca.'), id);
        console.error(error);
      }
    };

    loadDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mutation for sending bulk notifications
  const sendMutation = useMutation({
    mutationFn: async (data: SendNotificationFormData) => {
      // Build role filters
      const roleFilters: string[] = [];
      if (data.sendToTeachers) roleFilters.push(ROLES.PROFESOR);
      if (data.sendToPupils) roleFilters.push(ROLES.ELEV);

      // Fetch users with matching roles
      const page = await usersApi.getReport(
        {
          role: roleFilters as any, // Backend accepts array for multi-role filtering
          isEnabled: true,
        },
        {
          size: 9999, // Get all users
          page: 0,
        }
      );

      // Extract user IDs from paginated response
      const notificationData = {
        subject: data.subject,
        content: data.content,
        receiverIds: page.content.map((user: any) => user['id']),
      };

      const trimmedData = trimStringsFromAnywhere(notificationData);
      return notificationsApi.createBulk(trimmedData);
    },
    onSuccess: async () => {
      toastOneSuccess('Mesajul a fost trimis');
      const { data } = await refetchDefault();
      populeazaCuTemplate(data);
    },
    onError: (error) => {
      console.error('Notification send error:', error);

      // Map backend errors to form fields
      applyServerFieldErrors(error, form.setError);

      if (error?.message) {
        toastOneError(error.message);
      } else {
        toastOneError('Mesajul nu a putut fi trimis.');
      }
    },
  });

  // Form submission handler
  const onSubmit = async (data: SendNotificationFormData) => {
    const id = toast.loading('Mesajul se trimite...', { transition: Slide });

    try {
      await delay(1000);
      await sendMutation.mutateAsync(data);
      toast.dismiss(id);
    } catch (error: unknown) {
      updateFailureToast(getErrorMessage(error, 'Mesajul nu a putut fi trimis.'), id);
    }
  };

  // Cancel handler - reset form to default template
  const onCancel = async () => {
    const currentSubject = form.getValues('subject');
    const currentContent = form.getValues('content');

    if (!currentSubject && !currentContent) {
      toast.warning('Acest mesaj este deja gol.');
      return;
    }

    const id = toast.loading('Pagina se incarca...', { transition: Slide });
    try {
      await delay(1000);
      const { data } = await refetchDefault();
      populeazaCuTemplate(data);
      toast.dismiss(id);
    } catch (error: unknown) {
      updateFailureToast(getErrorMessage(error, 'Pagina nu s-a putut incarca.'), id);
    }
  };

  return {
    form,
    onSubmit,
    onCancel,
    isLoading,
    isSubmitting: sendMutation.isPending,
    isDirty: form.formState.isDirty, // Track unsaved changes for navigation blocking
  };
}
